/** ACTIVATED abilities: the ones that are a decision rather than a rule.
 *
 * Every other entry in `CreatureAbility` is passive or triggered. A stack with
 * FLYING flies whether or not anybody thinks about it; a stack with DEATH_STARE
 * rolls its gaze on every blow. The roster a player commands is therefore the
 * same roster on every turn, and the only per-turn choices in a battle come
 * from position, targets and the hero's one cast.
 *
 * H5's answer was abilities the player spends a turn on. This is that, with two
 * prices that are not negotiable and are what keep it from adding numbers
 * instead of decisions:
 *
 *   - it costs the stack's whole turn, so using it is always giving something
 *     up (the schema's floor is a two-round cooldown, and the effects below are
 *     priced at three and four);
 *   - it goes on `StackStore.cooldown`, a countdown in rounds ticked at each
 *     round start, so the answer to "may I?" is one integer and not a scan of
 *     the journal.
 *
 * The effects are a closed union, small on purpose. Each one is a thing the
 * kernel could already do, reached by a new route: `fortify` is Defend plus a
 * heal that spreads, `warcry` is a Battle Fury the creature casts on its own
 * side, `venom` is the POISON ability aimed by hand. Nothing here is a new
 * arithmetic bracket, which is the whole claim: decisions without numbers.
 */

import { asModifierSrcId } from "../ids.js";
import { clampInt, roundHalfAway } from "../numeric.js";
import type { Reason } from "../commands.js";
import { Err, Ok, type Result } from "../result.js";
import type { ContentRegistry, CreatureAbility } from "../content/registry.js";
import { F_WAR_MACHINE, type BattleState, isAlive } from "./state.js";
import { F_ACTED } from "./flags.js";
import { abilitiesOf, isMindImmune } from "./abilities.js";
import { stacksAdjacent } from "./move.js";
import { defendModifier, SRC_DEFEND } from "./damage.js";
import { F_DEFENDING } from "./state.js";
import { type Ctx, addMod, effectiveMaxHp, healStack, setFlags } from "./apply.js";
import { jSetI32 } from "./journal.js";

type Activated = Extract<CreatureAbility, { kind: "ACTIVATED" }>;

const SRC_WARCRY = asModifierSrcId("ability.warcry");
const SRC_VENOM = asModifierSrcId("ability.venom");

/** Rounds a `warcry` holds for. Short: it is a tempo swing, not a Prayer. */
const WARCRY_ROUNDS = 2;

export function activatedOf(content: ContentRegistry, s: BattleState, slot: number): Activated | null {
  if (slot < 0 || slot >= s.stacks.n) return null;
  for (const a of abilitiesOf(content, s.stacks.def[slot]!)) {
    if (a.kind === "ACTIVATED") return a;
  }
  return null;
}

export function validateActivate(
  s: BattleState, content: ContentRegistry, slot: number,
): Result<Activated, Reason> {
  if (!isAlive(s.stacks, slot)) return Err({ code: "STACK_DEAD", stack: slot });
  if ((s.stacks.flags[slot]! & F_ACTED) !== 0) return Err({ code: "STACK_NOT_ACTIVE", stack: slot });
  const a = activatedOf(content, s, slot);
  if (a === null) return Err({ code: "TARGET_INVALID" });
  // The countdown, not a stamp of the round it was last used: a loaded save can
  // start at any round number and a stamp would have to be compared against one.
  if (s.stacks.cooldown[slot]! > 0) return Err({ code: "NO_SHOTS_LEFT" });
  return Ok(a);
}

export function activate(
  c: Ctx, content: ContentRegistry, slot: number,
): Result<true, Reason> {
  const r = validateActivate(c.s, content, slot);
  if (!r.ok) return r;
  const a = r.value;
  const s = c.s;
  const st = s.stacks;
  const side = st.side[slot]!;

  switch (a.effect) {
    case "fortify": {
      // Defend, and mend what is standing beside it. The defend half goes
      // through the same modifier the DEFEND command installs, so `beginTurn`
      // strips it on the stack's next turn without knowing this ability exists.
      setFlags(c, slot, F_DEFENDING);
      addMod(c, slot, defendModifier());
      for (let i = 0; i < st.n; i++) {
        if (!isAlive(st, i) || st.side[i] !== side) continue;
        if (i !== slot && !stacksAdjacent(st, i, slot)) continue;
        // Mends, never raises: `resurrectTo` is the living count, so a fortify
        // brings a wounded rank back to full and never refills a dead one.
        healStack(c, i, a.power, st.count[i]!);
      }
      break;
    }
    case "warcry": {
      for (let i = 0; i < st.n; i++) {
        if (!isAlive(st, i) || st.side[i] !== side) continue;
        if ((st.flags[i]! & F_WAR_MACHINE) !== 0) continue;
        addMod(c, i, {
          source: SRC_WARCRY, stat: "attack", op: "add", value: a.power,
          stackGroup: SRC_WARCRY as string, stackRule: "highest",
          expires: { kind: "round", rounds: WARCRY_ROUNDS },
        });
      }
      break;
    }
    /**
     * Terrify: the morale of every adjacent enemy drops for the battle.
     *
     * Morale is a COLUMN, not a modifier - `rollGoodMorale` reads
     * `stacks.morale` directly - so this writes the column rather than adding a
     * `Modifier` nothing would read. Clamped to the classic's own -3..3 at the point of
     * writing, so no amount of terrifying takes a stack below the floor the
     * morale roll is defined on.
     *
     * The mindless are exempt for the reason they are exempt from every mind
     * rule: `moraleEligible` already refuses to roll morale for the undead and
     * for war machines, so a penalty on them would be a number nothing reads.
     */
    case "terrify": {
      for (let i = 0; i < st.n; i++) {
        if (!isAlive(st, i) || st.side[i] === side) continue;
        if (!stacksAdjacent(st, i, slot)) continue;
        if (isMindImmune(st.flags[i]!) || (st.flags[i]! & F_WAR_MACHINE) !== 0) continue;
        jSetI32(c.j, st.morale, i, clampInt(st.morale[i]! - a.power, -3, 3));
      }
      break;
    }
    case "venom": {
      // The POISON ability, aimed by hand rather than carried on a blow: a
      // share of every adjacent enemy's maximum hit points, for the battle.
      // `stackRule: "sum"` is POISON's own, so a second dose stacks the way a
      // second bite does.
      for (let i = 0; i < st.n; i++) {
        if (!isAlive(st, i) || st.side[i] === side) continue;
        if (!stacksAdjacent(st, i, slot)) continue;
        const bite = Math.max(1, roundHalfAway((effectiveMaxHp(s, i) * a.power) / 100));
        addMod(c, i, {
          source: SRC_VENOM, stat: "hp", op: "add", value: -bite,
          stackGroup: SRC_VENOM as string, stackRule: "sum",
          expires: { kind: "battle" },
        });
      }
      break;
    }
  }

  // Set after the effect, so a rule that reads the cooldown while resolving
  // sees the state the player pressed the button in.
  jSetI32(c.j, st.cooldown, slot, a.cooldownRounds);
  c.events.push({ type: "BattleAbilityActivated", stack: slot, effect: a.effect });
  return Ok(true);
}

void SRC_DEFEND;
